import React, { useState, useEffect, Suspense, useCallback } from "react"
import MainRouter from "./containers/MainRouter/MainRouter"
import Keycloak from "keycloak-js"
import AuthContext from "./context/auth-context"
import SelectedCompanyContext, { parseCompanyKeysClaim } from "./context/selected-company-context"
import axios from "axios"
import Favicon from "./containers/Favicon/Favicon"
import "./i18n"
import { datadogRum } from '@datadog/browser-rum'
import OfflineWarningScreen from "./components/common/OfflineWarningScreen"

const SELECTED_COMPANY_STORAGE_PREFIX = "selected_company_key:"
 
function App() {

  function setUserInDatadog(id, name, email) {
    datadogRum.setUser({
      id,
      name,
      email,
    });
  }

  axios.defaults.baseURL = process.env.REACT_APP_DASH_API_URL + ":" + process.env.REACT_APP_DASH_API_PORT
  const [keyCloakState, setKeyCloakState] = useState({ keycloak: null, authenticated: false })

  if (process.env.REACT_APP_ENV !== 'local') {
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'client-dashboard',
      env: process.env.REACT_APP_ENV,
      sampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true
    })

    datadogRum.startSessionReplayRecording()
  }

  const [language, setLanguage] = useState()
  const [maxSessionTime, setMaxSessionTime] = useState(null)

  useEffect(() => {
    setMaxSessionTime(Date.now() + (12 * 3600 * 1000)) // 12 hours
    localStorage.removeItem("@kyc_session");
    sessionStorage.clear();
  }, [])

  useEffect(() => {
    if (keyCloakState.keycloak) {
      setInterval(() => {
        if (Date.now() > maxSessionTime) {
          localStorage.removeItem("@kyc_session");
          sessionStorage.clear();
          keyCloakState.keycloak.logout()
        } else if (Date.now() - (40 * 1000) >= keyCloakState.keycloak.tokenParsed.exp) {
          keyCloakState.keycloak.updateToken(40).then((updated) => { // If the token will expire in 30 seconds
            if (updated) {
              setKeyCloakState({ keycloak: keyCloakState.keycloak, authenticated: updated, user_data: keyCloakState.user_data })
            }
          })
        }
      }, 5 * 1000); // At each 5 seconds
    }
  }, [keyCloakState, maxSessionTime])

  const [authorized, setAuthorized] = useState(false);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState(null);
  const [companiesMap, setCompaniesMap] = useState({});

  const companyKeys = keyCloakState.keycloak
    ? parseCompanyKeysClaim(keyCloakState.keycloak.tokenParsed?.company_keys)
    : [];
  const isCompanyKeysUser = companyKeys.length > 0;

  const persistSelectedCompanyKey = useCallback((userKey, companyKey) => {
    if (!userKey || !companyKey) return;
    try {
      localStorage.setItem(SELECTED_COMPANY_STORAGE_PREFIX + userKey, companyKey);
    } catch (e) {
    }
  }, []);

  const handleSetSelectedCompanyKey = useCallback((nextKey) => {
    if (!isCompanyKeysUser || !companyKeys.includes(nextKey)) return;
    setSelectedCompanyKey(nextKey);
    persistSelectedCompanyKey(keyCloakState.user_data?.user_key, nextKey);
  }, [isCompanyKeysUser, companyKeys, keyCloakState.user_data, persistSelectedCompanyKey]);

  useEffect(() => {
    if (!keyCloakState.keycloak) {
      setSelectedCompanyKey(null);
      return;
    }
    if (!isCompanyKeysUser) {
      setSelectedCompanyKey(null);
      return;
    }
    const userKey = keyCloakState.user_data?.user_key;
    let stored = null;
    try {
      stored = userKey ? localStorage.getItem(SELECTED_COMPANY_STORAGE_PREFIX + userKey) : null;
    } catch (e) {
      stored = null;
    }
    const initial = stored && companyKeys.includes(stored) ? stored : companyKeys[0];
    setSelectedCompanyKey(initial);
    persistSelectedCompanyKey(userKey, initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyCloakState.keycloak, isCompanyKeysUser, companyKeys.join("|")])

  useEffect(() => {
    if (keyCloakState.keycloak) {
      axios.defaults.headers = { "Authorization": keyCloakState.keycloak.token };
      setAuthorized(isCompanyKeysUser ? Boolean(selectedCompanyKey) : true);
    } else {
      axios.defaults.headers = { "Authorization": null };
      setAuthorized(false);
    }
  }, [keyCloakState, isCompanyKeysUser, selectedCompanyKey])

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      if (isCompanyKeysUser && selectedCompanyKey) {
        const headers = config.headers || {};
        const alreadyHasCompanyKey = Object.keys(headers).some(
          (name) => name.toLowerCase().replace(/[-_]/g, "") === "companykey"
        );
        if (!alreadyHasCompanyKey) {
          config.headers = { ...headers, "COMPANY-KEY": selectedCompanyKey };
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptorId);
  }, [isCompanyKeysUser, selectedCompanyKey])

  useEffect(() => {
    if (!keyCloakState.keycloak || !isCompanyKeysUser || !selectedCompanyKey) {
      setCompaniesMap({});
      return;
    }
    let cancelled = false;
    axios.get('/dash/business_group/companies').then((response) => {
      if (cancelled) return;
      const companies = response?.data?.companies || [];
      const map = {};
      companies.forEach((company) => {
        if (company?.company_key) {
          map[company.company_key] = company.name || company.legal_name || company.company_key;
        }
      });
      setCompaniesMap(map);
    }).catch(() => {
      if (!cancelled) setCompaniesMap({});
    });
    return () => { cancelled = true; };
  }, [keyCloakState.keycloak, isCompanyKeysUser, selectedCompanyKey])

  useEffect(() => {
    const keycloak = Keycloak("/keycloak.json")
    keycloak
      .init({ onLoad: "login-required" })
      .then(authenticated => {
        let user_data = {}
        user_data["username"] = keycloak.tokenParsed.preferred_username
        user_data["company_key"] = keycloak.tokenParsed.company_key
        user_data["business_group_key"] = keycloak.tokenParsed.business_group_key
        user_data["email"] = keycloak.tokenParsed.email
        user_data["name"] = keycloak.tokenParsed.name
        user_data["first_name"] = keycloak.tokenParsed.given_name
        user_data["last_name"] = keycloak.tokenParsed.family_name
        user_data["roles"] = keycloak.tokenParsed.realm_access.roles
        user_data["user_key"] = keycloak.tokenParsed.sub
        user_data["locale"] = keycloak.tokenParsed.locale
        user_data["rental_store"] = keycloak.tokenParsed.rental_store;
        user_data["company_keys"] = parseCompanyKeysClaim(keycloak.tokenParsed.company_keys);

        setUserInDatadog(user_data?.user_key, user_data?.first_name + ' ' + user_data?.last_name, user_data?.email)

        keycloak.tokenParsed.locale ? setLanguage(keycloak.tokenParsed.locale) : setLanguage('pt-BR')
        setKeyCloakState({
          keycloak: keycloak,
          authenticated: authenticated,
          user_data: { ...user_data }
        })
      })
      .catch(() => {
        console.log("Keycloak Authentication catch")

        setKeyCloakState({ keycloak: null, authenticated: null, user_data: null })
      })
  }, [])

  let main = null
  if (!keyCloakState.keycloak) {
    main = (
      <div />
    )
  } else if (keyCloakState.authenticated && authorized) {
    const baseUserData = keyCloakState.user_data || {};
    const effectiveUserData = isCompanyKeysUser
      ? {
          ...baseUserData,
          company_key: selectedCompanyKey,
          business_group_key: null,
          is_company_keys_user: true,
        }
      : { ...baseUserData, is_company_keys_user: false };
    main = (
      <AuthContext.Provider
        value={{
          keycloak_adapter: keyCloakState.keycloak,
          authenticated: keyCloakState.authenticated,
          user_data: effectiveUserData,
        }}
      >
        <SelectedCompanyContext.Provider
          value={{
            is_company_keys_user: isCompanyKeysUser,
            company_keys: companyKeys,
            selected_company_key: selectedCompanyKey,
            setSelectedCompanyKey: handleSetSelectedCompanyKey,
            companies_map: companiesMap,
          }}
        >
          <Favicon />
          <Suspense falllback={<div>Loading ...</div>}>
              <OfflineWarningScreen $hide={true/*offlineTicks < 3*/}/>
              <MainRouter
                key={isCompanyKeysUser ? `ck:${selectedCompanyKey}` : 'default'}
                language={language}
                setLanguage={setLanguage}
              />
          </Suspense>
        </SelectedCompanyContext.Provider>
      </AuthContext.Provider>
    )
  } else {
    main = (
      <div
        style={{
          backgroundColor: "#000000",
          height: "auto",
          width: "auto",
          color: "#FFFFFF"
        }}
      >
        Autenticação falhou!!!
      </div>
    )
  }

  return (
    <div className="App">
      {main}
    </div>
  )
}
export default App
